import { useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "destructive";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  fullWidth?: boolean;
}

const VARIANT_STYLE: Record<Variant, React.CSSProperties> = {
  primary: {
    appearance: "none",
    background: "var(--color-primary)",
    color: "var(--color-on-primary, var(--color-primary-foreground, var(--color-text-inverse)))",
    WebkitTextFillColor: "var(--color-on-primary, var(--color-primary-foreground, var(--color-text-inverse)))",
    border: "1px solid transparent",
  },
  secondary: {
    background: "var(--color-panel)",
    color: "var(--color-text)",
    border: "1px solid var(--color-border-strong)",
  },
  ghost: {
    background: "transparent",
    color: "var(--color-text)",
    border: "1px solid transparent",
  },
  danger: {
    background: "var(--color-danger)",
    color: "var(--color-danger-foreground, var(--color-text-inverse))",
    WebkitTextFillColor: "var(--color-danger-foreground, var(--color-text-inverse))",
    border: "1px solid transparent",
  },
  destructive: {
    background: "var(--color-danger)",
    color: "var(--color-danger-foreground, var(--color-text-inverse))",
    WebkitTextFillColor: "var(--color-danger-foreground, var(--color-text-inverse))",
    border: "1px solid transparent",
  },
};

const SIZE_STYLE: Record<Size, React.CSSProperties> = {
  sm: { padding: "6px 12px", fontSize: "var(--font-size-sm)", minHeight: 32 },
  md: { padding: "10px 18px", fontSize: "var(--font-size-md)", minHeight: "var(--touch-target)" },
  lg: { padding: "14px 24px", fontSize: "var(--font-size-lg)", minHeight: 52 },
};

/**
 * Primary interactive control. Pill-shaped, soft-shadow, momentum-forward.
 *
 * Single-flight: when `onClick` returns a promise (write `onClick={() => save()}`, not
 * `() => void save()`), the button disables itself until that promise settles. A ref lock
 * rejects a second click that lands before React re-renders, so double clicks on a slow
 * server cannot submit twice.
 */
export function Button({
  variant = "primary",
  size = "md",
  icon,
  fullWidth,
  children,
  style,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  const [pending, setPending] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (locked.current) {
      event.preventDefault();
      return;
    }
    const result: unknown = onClick?.(event);
    if (result && typeof (result as PromiseLike<unknown>).then === "function") {
      locked.current = true;
      setPending(true);
      const release = () => {
        locked.current = false;
        if (mounted.current) setPending(false);
      };
      (result as PromiseLike<unknown>).then(release, release);
    }
  }

  const blocked = disabled || pending;
  return (
    <button
      {...rest}
      onClick={handleClick}
      aria-busy={pending || undefined}
      data-variant={variant}
      disabled={blocked}
      style={{
        appearance: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-2)",
        borderRadius: "var(--radius-pill)",
        fontWeight: "var(--font-weight-medium)",
        fontFamily: "var(--font-sans)",
        cursor: blocked ? "not-allowed" : "pointer",
        opacity: blocked ? 0.55 : 1,
        boxShadow: variant === "primary" ? "var(--shadow-sm)" : "none",
        transition: "transform var(--duration-fast) var(--ease-standard), box-shadow var(--duration-standard) var(--ease-standard)",
        width: fullWidth ? "100%" : undefined,
        ...VARIANT_STYLE[variant],
        ...SIZE_STYLE[size],
        ...style,
      }}
      onMouseDown={(e) => {
        if (!blocked) e.currentTarget.style.transform = "scale(0.98)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      {icon}
      {children}
    </button>
  );
}
